/**
 * Unified Store Orders Page
 *
 * Consolidates all order types:
 * - Mill Requests (items to pull from store for shipping orders)
 * - Locker orders (assigned to pickup lockers)
 * - In-Store orders (counter pickup)
 * - Today's Pickups (all pickups scheduled today)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import StoreHeader from '../../components/Layout/StoreHeader';
import AssignLockerModal from '../../components/Store/AssignLockerModal';
import api from '../../services/api';
import { printBCOrderReceipt } from '../../services/receiptPrinter';
import SubstituteItemModal from '../../components/Store/SubstituteItemModal';

type OrderTab = 'all' | 'mill_requests' | 'locker' | 'in_store' | 'today';

// Simple notification sound (beep using Web Audio API)
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Friendly "ding-dong" pattern
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.15);
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.4);
  } catch (e) {
    console.log('Audio not available');
  }
};

interface OrderItem {
  id: number;
  name: string;
  quantity: number;
  unit_price: number;
  total: number;
  location?: string;
}

interface LockerAssignment {
  id: number;
  locker_number: number;
  locker_name: string;
  passcode: string;
  assigned_at: string;
  status: string;
}

interface PickupOrder {
  id: number;
  // Which table this row came from. The pickup queue merges StoreClubOrder
  // and BridgeClubOrder on raw PK, so anything that targets a BC-only
  // endpoint MUST check this first — /store/orders/<id>/receipt/ resolves
  // BridgeClubOrder by pk and would otherwise be handed a StoreClubOrder id.
  order_source: 'store_club' | 'bridge_club_local';
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  pickup_method: 'in_store' | 'locker';
  status: string;
  items: OrderItem[];
  items_summary: string;
  total: number;
  created_at: string;
  paid_at: string | null;
  locker_assignment: LockerAssignment | null;
  needs_locker: boolean;
}

interface MillRequest {
  id: number;
  order_number: string;
  customer_name: string;
  ship_date: string;
  ship_time: string;
  items: OrderItem[];
  status: 'pending' | 'pulled' | 'completed';
  priority: 'normal' | 'urgent';
  created_at: string;
  notes?: string;
}

interface Locker {
  id: number;
  number: number;
  name: string;
  capacity_bags: number;
  is_occupied: boolean;
}

interface Stats {
  total: number;
  mill_requests: number;
  locker_orders: number;
  instore_orders: number;
  today_pickups: number;
}

const StoreOrders: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check URL param for initial tab
  const initialTab = (searchParams.get('tab') as OrderTab) || 'all';
  const [activeTab, setActiveTab] = useState<OrderTab>(initialTab);
  const [orders, setOrders] = useState<PickupOrder[]>([]);
  const [millRequests, setMillRequests] = useState<MillRequest[]>([]);
  const [lockers, setLockers] = useState<Locker[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PickupOrder | null>(null);
  // Which job the shared AssignLockerModal is doing. 'assign' is the
  // original flow (order is ALREADY a locker order, it just has no locker
  // yet). 'switch' moves an in-store order into a locker, which is a
  // different endpoint because the assign endpoints reject non-locker
  // orders — flipping pickup_method is exactly what the switch does.
  const [lockerModalMode, setLockerModalMode] = useState<'assign' | 'switch'>('assign');
  // Order whose line is being substituted. Null = modal closed.
  const [substituteOrder, setSubstituteOrder] = useState<PickupOrder | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  // Order id currently being sent to the thermal printer (disables its button).
  const [printingId, setPrintingId] = useState<number | null>(null);

  // New order notification state
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const lastOrderCountRef = useRef<number>(0);
  const isFirstLoadRef = useRef<boolean>(true);

  // New mill-request notification state (same ding-dong + bounce as new orders)
  const [newMillAlert, setNewMillAlert] = useState(false);
  const [newMillCount, setNewMillCount] = useState(0);
  const lastMillCountRef = useRef<number>(0);
  const isFirstMillLoadRef = useRef<boolean>(true);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/store/pickup-orders/');
      const orderList = response.data.orders || [];
      setOrders(orderList);

      // Detect new orders (only after first load)
      const currentCount = orderList.length;
      if (!isFirstLoadRef.current && currentCount > lastOrderCountRef.current) {
        const newCount = currentCount - lastOrderCountRef.current;
        setNewOrderCount(newCount);
        setNewOrderAlert(true);
        playNotificationSound();
      }
      lastOrderCountRef.current = currentCount;
      isFirstLoadRef.current = false;

      // Calculate stats
      const lockerOrders = orderList.filter((o: PickupOrder) => o.pickup_method === 'locker');
      const instoreOrders = orderList.filter((o: PickupOrder) => o.pickup_method === 'in_store');
      const today = new Date().toDateString();
      const todayPickups = orderList.filter((o: PickupOrder) =>
        new Date(o.created_at).toDateString() === today
      );

      setStats({
        total: orderList.length,
        mill_requests: millRequests.length,
        locker_orders: lockerOrders.length,
        instore_orders: instoreOrders.length,
        today_pickups: todayPickups.length,
      });

      setError('');
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    }
  }, [millRequests.length]);

  const fetchMillRequests = useCallback(async () => {
    try {
      const response = await api.get('/store/mill-requests/');
      const list = response.data.requests || [];
      setMillRequests(list);

      // Ring the same ding-dong + bounce popup as new pickup orders when a new mill
      // pull request lands (e.g. the midnight optimizer's morning list). First load
      // primes the baseline silently.
      const currentCount = list.length;
      if (!isFirstMillLoadRef.current && currentCount > lastMillCountRef.current) {
        setNewMillCount(currentCount - lastMillCountRef.current);
        setNewMillAlert(true);
        playNotificationSound();
      }
      lastMillCountRef.current = currentCount;
      isFirstMillLoadRef.current = false;
    } catch (err: any) {
      // Endpoint may not exist yet - that's OK
      console.log('Mill requests endpoint not available');
      setMillRequests([]);
    }
  }, []);

  const fetchLockers = useCallback(async () => {
    try {
      const response = await api.get('/store/lockers/dashboard/');
      setLockers(response.data.lockers || []);
    } catch (err: any) {
      console.error('Error fetching lockers:', err);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchOrders(), fetchMillRequests(), fetchLockers()]);
      setLoading(false);
    };

    loadData();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchOrders();
      fetchMillRequests();
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, navigate, fetchOrders, fetchMillRequests, fetchLockers]);

  // Update stats when mill requests change
  useEffect(() => {
    if (stats) {
      setStats(prev => prev ? { ...prev, mill_requests: millRequests.length } : null);
    }
  }, [millRequests.length]);

  const handleMarkMillRequestPulled = async (requestId: number) => {
    try {
      const response = await api.post(`/store/mill-requests/${requestId}/complete/`);
      const msg = response.data?.message || 'Items marked as pulled';
      setToast({ type: 'success', message: msg });
      fetchMillRequests();
    } catch (err: any) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Failed to update request' });
    }
  };

  // Locker assignment now lives entirely in <AssignLockerModal>; we just
  // refresh + toast on success. The shared component hits the manual
  // endpoint (assign-locker-manual) under the hood — the legacy auto
  // endpoint is intentionally not used here anymore (Eufy sidecar P2P
  // is too unreliable for the happy-path UI to depend on).
  const handleAssignSuccess = (result: {
    customer_email: string | null;
    locker_number: number | string;
    email_sent?: boolean;
  }) => {
    const moved = lockerModalMode === 'switch';
    const what = moved
      ? `Order moved to Locker ${result.locker_number}`
      : `Locker ${result.locker_number} assigned`;
    // Keyed off email_sent, NOT the mere presence of an address. The old
    // check said "Customer notified at …" whenever an email existed, so a
    // failed send read as success and nobody phoned — leaving the customer
    // with a locker they had no code for. Same rule as the substitution
    // toast. `undefined` means the endpoint does not report it; fall back to
    // the neutral wording rather than crying failure.
    if (result.email_sent === false) {
      setToast({
        type: 'error',
        message: `${what}, but the customer was NOT emailed their code. Phone them.`,
      });
    } else if (result.email_sent && result.customer_email) {
      setToast({
        type: 'success',
        message: `${what}. Customer notified at ${result.customer_email}.`,
      });
    } else {
      setToast({ type: 'success', message: `${what}.` });
    }
    setShowAssignModal(false);
    setSelectedOrder(null);
    setLockerModalMode('assign');
    // Refetch so the card re-renders as a locker order: pickup_method and
    // locker_assignment both change server-side, and the card keys its
    // buttons off them.
    fetchOrders();
    fetchLockers();
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedOrder(null);
    setLockerModalMode('assign');
  };

  // Move an in-store Bridge Club order into a locker. Reuses the same
  // picker as ordinary assignment — staff still choose the locker and key
  // the code into the Eufy app — but points it at the switch endpoint,
  // which flips pickup_method, assigns, audits, and sends the "we moved
  // your order" email in one call.
  const handleOpenSwitchToLocker = (order: PickupOrder) => {
    setSelectedOrder(order);
    setLockerModalMode('switch');
    setShowAssignModal(true);
  };

  const handleMarkPickedUp = async (orderId: number) => {
    try {
      await api.post(`/store/pickup-orders/${orderId}/complete/`);
      setToast({ type: 'success', message: 'Order marked as picked up' });
      fetchOrders();
    } catch (err: any) {
      setToast({ type: 'error', message: 'Failed to update order' });
    }
  };

  // Print a thermal receipt for a paid-online Bridge Club order. Mirrors the
  // POS print path (StorePOS.handlePrintReceipt): surface err.message, fall
  // back to the same QZ Tray hint, never let the rejection go unhandled.
  const handlePrintReceipt = async (order: PickupOrder) => {
    try {
      setPrintingId(order.id);
      const response = await api.get(`/store/orders/${order.id}/receipt/`);
      await printBCOrderReceipt(response.data);
      setToast({ type: 'success', message: `Receipt printed for ${order.order_number}` });
    } catch (err: any) {
      console.error('Print failed:', err);
      setToast({
        type: 'error',
        message:
          err.response?.data?.error ||
          err.message ||
          'Failed to print. Is QZ Tray running?',
      });
    } finally {
      setPrintingId(null);
    }
  };

  // Substitution settles money at the counter, so the toast repeats the
  // server's own wording rather than re-deriving it — the amount staff acts
  // on must be the amount the server recorded.
  const handleSubstituteSuccess = (result: any) => {
    // Same shape as the locker switch's toast, but keyed off the server's
    // email_sent flag rather than merely having an address on file. Money
    // moved here, so if the customer was NOT told, staff must notice and
    // phone — hence the error styling even though the substitution itself
    // succeeded. The message always leads with the substitution result so
    // nobody reads the red toast as "it failed".
    if (result.email_sent) {
      setToast({
        type: 'success',
        message: `${result.message} Customer notified at ${result.customer_email}.`,
      });
    } else {
      const why = result.email_reason === 'no_customer_email'
        ? 'no email address on file'
        : 'the email failed to send';
      setToast({
        type: 'error',
        message: `${result.message} NOT emailed — ${why}. Phone the customer.`,
      });
    }
    setSubstituteOrder(null);
    fetchOrders();
  };

  const handleMarkReady = async (orderId: number) => {
    try {
      await api.post(`/store/pickup-orders/${orderId}/mark-ready/`);
      setToast({ type: 'success', message: 'Order marked as ready - customer notified' });
      fetchOrders();
    } catch (err: any) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Failed to mark ready' });
    }
  };

  const getFilteredOrders = (): PickupOrder[] => {
    const today = new Date().toDateString();
    switch (activeTab) {
      case 'locker':
        return orders.filter(o => o.pickup_method === 'locker');
      case 'in_store':
        return orders.filter(o => o.pickup_method === 'in_store');
      case 'today':
        return orders.filter(o => new Date(o.created_at).toDateString() === today);
      default:
        return orders;
    }
  };

  const filteredOrders = getFilteredOrders();

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  if (loading) {
    return (
      <div style={styles.page}>
        <StoreHeader />
        <div style={styles.container}>
          <div style={styles.loading}>Loading orders...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <StoreHeader />

      {/* Toast Notification */}
      {toast && (
        <div style={{
          ...styles.toast,
          backgroundColor: toast.type === 'success' ? '#d4edda' : '#f8d7da',
          color: toast.type === 'success' ? '#155724' : '#721c24',
        }}>
          {toast.message}
        </div>
      )}

      {/* New Order Alert Popup */}
      {newOrderAlert && (
        <div style={styles.newOrderOverlay} onClick={() => setNewOrderAlert(false)}>
          <div style={styles.newOrderPopup} onClick={e => e.stopPropagation()}>
            <div style={styles.newOrderIcon}>🔔</div>
            <div style={styles.newOrderTitle}>
              {newOrderCount === 1 ? 'New Order!' : `${newOrderCount} New Orders!`}
            </div>
            <div style={styles.newOrderSubtitle}>
              {newOrderCount === 1
                ? 'A new order has been placed'
                : `${newOrderCount} new orders have been placed`}
            </div>
            <button
              style={styles.newOrderButton}
              onClick={() => setNewOrderAlert(false)}
            >
              View Orders
            </button>
          </div>
        </div>
      )}

      {newMillAlert && (
        <div style={styles.newOrderOverlay} onClick={() => setNewMillAlert(false)}>
          <div style={styles.newOrderPopup} onClick={e => e.stopPropagation()}>
            <div style={styles.newOrderIcon}>🏭</div>
            <div style={styles.newOrderTitle}>
              {newMillCount === 1 ? 'New Mill Pull Request!' : `${newMillCount} New Mill Pull Requests!`}
            </div>
            <div style={styles.newOrderSubtitle}>
              {newMillCount} new mill pull request{newMillCount !== 1 ? 's' : ''} — pull before opening
            </div>
            <button
              style={styles.newOrderButton}
              onClick={() => { setNewMillAlert(false); setActiveTab('mill_requests'); }}
            >
              View Mill Requests
            </button>
          </div>
        </div>
      )}

      <div style={styles.container}>
        {/* Mill Requests Alert Banner */}
        {millRequests.length > 0 && (
          <div style={styles.millRequestBanner}>
            <div style={styles.millRequestIcon}>🏭</div>
            <div style={styles.millRequestContent}>
              <div style={styles.millRequestTitle}>
                {millRequests.length} Mill Request{millRequests.length !== 1 ? 's' : ''} - Pull Before Opening
              </div>
              <div style={styles.millRequestSubtitle}>
                These items are needed for shipping orders today
              </div>
            </div>
            <button
              style={styles.viewMillRequestsBtn}
              onClick={() => setActiveTab('mill_requests')}
            >
              View Requests
            </button>
          </div>
        )}

        {/* Page Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Store Orders</h1>
          <div style={styles.statsRow}>
            <span style={styles.statBadge}>{stats?.total || 0} Total</span>
            {(stats?.mill_requests || 0) > 0 && (
              <span style={{ ...styles.statBadge, ...styles.statBadgeMill }}>
                {stats?.mill_requests} Mill
              </span>
            )}
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Tabs */}
        <div style={styles.tabBar}>
          <button
            style={activeTab === 'all' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('all')}
          >
            All ({orders.length})
          </button>
          <button
            style={activeTab === 'mill_requests' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('mill_requests')}
          >
            Mill Requests ({millRequests.length})
          </button>
          <button
            style={activeTab === 'locker' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('locker')}
          >
            Locker ({orders.filter(o => o.pickup_method === 'locker').length})
          </button>
          <button
            style={activeTab === 'in_store' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('in_store')}
          >
            In-Store ({orders.filter(o => o.pickup_method === 'in_store').length})
          </button>
          <button
            style={activeTab === 'today' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('today')}
          >
            Today
          </button>
        </div>

        {/* Mill Requests Tab Content */}
        {activeTab === 'mill_requests' && (
          <div style={styles.orderList}>
            {millRequests.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>✓</div>
                <div style={styles.emptyText}>No pending mill requests</div>
                <div style={styles.emptySubtext}>All items have been pulled</div>
              </div>
            ) : (
              millRequests.map((request) => (
                <div key={request.id} style={styles.millRequestCard}>
                  <div style={styles.millRequestHeader}>
                    <div>
                      <span style={styles.millRequestBadge}>🏭 MILL REQUEST</span>
                      {request.priority === 'urgent' && (
                        <span style={styles.urgentBadge}>URGENT</span>
                      )}
                    </div>
                    <div style={styles.millRequestOrderInfo}>
                      Order #{request.order_number} | {request.customer_name}
                    </div>
                    <div style={styles.millRequestShipInfo}>
                      Ships: {request.ship_date} {request.ship_time && `at ${request.ship_time}`}
                    </div>
                  </div>
                  <div style={styles.millRequestItems}>
                    <div style={styles.millRequestItemsTitle}>PULL FROM STORE:</div>
                    {request.items.map((item, idx) => (
                      <div key={idx} style={styles.millRequestItem}>
                        <span style={styles.itemCheckbox}>☐</span>
                        <span style={styles.itemQty}>{item.quantity}x</span>
                        <span style={styles.itemName}>{item.name}</span>
                        {item.location && (
                          <span style={styles.itemLocation}>({item.location})</span>
                        )}
                      </div>
                    ))}
                  </div>
                  {request.notes && (
                    <div style={styles.millRequestNotes}>
                      Note: {request.notes}
                    </div>
                  )}
                  <div style={styles.millRequestActions}>
                    <button
                      style={styles.markPulledBtn}
                      onClick={() => handleMarkMillRequestPulled(request.id)}
                    >
                      ✓ Mark All Pulled
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Regular Orders Tab Content */}
        {activeTab !== 'mill_requests' && (
          <div style={styles.orderList}>
            {filteredOrders.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>📦</div>
                <div style={styles.emptyText}>No orders found</div>
                <div style={styles.emptySubtext}>
                  {activeTab === 'all'
                    ? 'No pending orders at this time'
                    : `No ${activeTab.replace('_', ' ')} orders`}
                </div>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <div key={order.id} style={styles.orderCard}>
                  <div style={styles.orderHeader}>
                    <div style={styles.orderNumber}>#{order.order_number}</div>
                    <div style={styles.orderBadges}>
                      <span style={order.pickup_method === 'locker' ? styles.lockerBadge : styles.instoreBadge}>
                        {order.pickup_method === 'locker' ? '🔐 Locker' : '🏪 In-Store'}
                      </span>
                      {order.locker_assignment && (
                        <span style={styles.lockerNumberBadge}>
                          Locker {order.locker_assignment.locker_number}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={styles.orderCustomer}>
                    <div style={styles.customerName}>{order.customer_name}</div>
                    {order.customer_phone && (
                      <div style={styles.customerPhone}>{order.customer_phone}</div>
                    )}
                  </div>
                  <div style={styles.orderItems}>
                    {order.items_summary || `${order.items.length} item(s)`}
                  </div>
                  <div style={styles.orderTotal}>
                    Total: ${order.total.toFixed(2)}
                  </div>
                  {/* Status label — helps staff understand non-default states
                      (Loaded in POS / Awaiting Payment / Ready). 'paid' is the
                      default queue state and needs no label. */}
                  {order.status !== 'paid' && (
                    <div style={styles.orderStatusLabel}>
                      Status: {(({
                        awaiting_payment: 'Awaiting Payment',
                        ready_for_pickup: 'Ready for Pickup',
                        loaded_in_pos: 'Loaded in POS',
                      } as Record<string, string>)[order.status]) || order.status.replace(/_/g, ' ')}
                    </div>
                  )}
                  <div style={styles.orderActions}>
                    {/* Locker orders: Assign Locker button */}
                    {order.pickup_method === 'locker' && !order.locker_assignment && (
                      <button
                        style={styles.assignBtn}
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowAssignModal(true);
                        }}
                      >
                        Assign Locker
                      </button>
                    )}
                    {/* Show passcode if locker assigned */}
                    {order.locker_assignment && (
                      <div style={styles.passcodeDisplay}>
                        Code: <strong>{order.locker_assignment.passcode}</strong>
                      </div>
                    )}
                    {/* In-store orders: Mark Ready button. Allowed from 'paid'
                        and pay-in-store 'awaiting_payment' — matches the set
                        store_mark_ready_instore accepts. */}
                    {order.pickup_method === 'in_store' && ['paid', 'awaiting_payment'].includes(order.status) && (
                      <button
                        style={styles.readyBtn}
                        onClick={() => handleMarkReady(order.id)}
                      >
                        📦 Mark Ready
                      </button>
                    )}
                    {/* Status badge for ready orders */}
                    {order.status === 'ready_for_pickup' && (
                      <span style={styles.readyBadge}>✓ Ready</span>
                    )}
                    {/* Mark Picked Up — allowed from the statuses the backend
                        store_complete_pickup accepts: ready_for_pickup, the
                        till "loaded_in_pos" path, and direct 'paid' pickups.
                        (Previously only ready_for_pickup, which stranded
                        loaded_in_pos / pay-in-store orders in the queue.) */}
                    {['ready_for_pickup', 'loaded_in_pos', 'paid'].includes(order.status) && (
                      <button
                        style={styles.pickedUpBtn}
                        onClick={() => handleMarkPickedUp(order.id)}
                      >
                        ✓ Picked Up
                      </button>
                    )}
                    {/* Substitute — Bridge Club orders only, and only while
                        the order can still change. Same order_source gate as
                        Print Receipt and Move to Locker, load-bearing for the
                        same reason: the pickup queue merges two tables on raw
                        PK and this endpoint resolves BridgeClubOrder by pk. */}
                    {order.order_source === 'bridge_club_local'
                      && order.status !== 'picked_up'
                      && order.status !== 'cancelled'
                      && order.items.length > 0 && (
                      <button
                        style={styles.substituteBtn}
                        onClick={() => setSubstituteOrder(order)}
                      >
                        ⇄ Substitute
                      </button>
                    )}

                    {/* Move to Locker — Bridge Club IN-STORE orders only.
                        Same order_source gate as Print Receipt and load-bearing
                        for the same reason: the switch endpoint resolves
                        BridgeClubOrder by pk, so a StoreClubOrder row would
                        target the wrong order on a PK collision. The
                        pickup_method check keeps it off orders that are
                        already locker orders (the switch is one-way). */}
                    {order.order_source === 'bridge_club_local'
                      && order.pickup_method === 'in_store'
                      && order.status !== 'picked_up'
                      && order.status !== 'cancelled' && (
                      <button
                        style={styles.moveToLockerBtn}
                        onClick={() => handleOpenSwitchToLocker(order)}
                      >
                        📦 Move to Locker
                      </button>
                    )}

                    {/* Print Receipt — Bridge Club orders only. The receipt
                        endpoint resolves BridgeClubOrder by pk, so offering
                        this on a StoreClubOrder row would print the wrong
                        order (or 404) on a PK collision. */}
                    {order.order_source === 'bridge_club_local' && (
                      <button
                        style={{
                          ...styles.printBtn,
                          ...(printingId === order.id ? styles.printBtnBusy : {}),
                        }}
                        onClick={() => handlePrintReceipt(order)}
                        disabled={printingId === order.id}
                      >
                        {printingId === order.id ? 'Printing…' : '🧾 Print Receipt'}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Assign Locker — shared component (see PickupOrders.tsx for
          rationale). Same modal everywhere so staff workflow doesn't
          drift between /store/orders and /store/pickup-orders. */}
      {substituteOrder && (
        <SubstituteItemModal
          order={{
            id: substituteOrder.id,
            order_number: substituteOrder.order_number,
            customer_name: substituteOrder.customer_name,
            items: substituteOrder.items.map(i => ({
              id: i.id,
              name: i.name,
              quantity: i.quantity,
            })),
          }}
          onSuccess={handleSubstituteSuccess}
          onClose={() => setSubstituteOrder(null)}
        />
      )}

      {showAssignModal && selectedOrder && (
        <AssignLockerModal
          order={{
            id: selectedOrder.id,
            order_number: selectedOrder.order_number,
            customer_name: selectedOrder.customer_name,
          }}
          lockers={lockers.map(l => ({
            id: l.id,
            number: l.number,
            name: l.name,
            is_occupied: l.is_occupied,
          }))}
          endpoint={
            lockerModalMode === 'switch'
              ? `/store/bc-orders/${selectedOrder.id}/switch-to-locker/`
              : undefined
          }
          title={lockerModalMode === 'switch' ? 'Move to Locker' : undefined}
          submitLabel={
            lockerModalMode === 'switch'
              ? 'Move to Locker & Email Customer'
              : undefined
          }
          submittingLabel={lockerModalMode === 'switch' ? 'Moving…' : undefined}
          successTitle={
            lockerModalMode === 'switch' ? 'Order Moved to Locker' : undefined
          }
          onSuccess={handleAssignSuccess}
          onClose={closeAssignModal}
        />
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '1.5rem',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
    fontSize: '1.1rem',
  },
  toast: {
    position: 'fixed',
    top: '80px',
    right: '20px',
    padding: '1rem 1.5rem',
    borderRadius: '8px',
    fontWeight: '600',
    zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  // Mill Request Banner
  millRequestBanner: {
    backgroundColor: '#fff3e0',
    borderLeft: '4px solid #ff9800',
    borderRadius: '8px',
    padding: '1rem 1.5rem',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  millRequestIcon: {
    fontSize: '2rem',
  },
  millRequestContent: {
    flex: 1,
  },
  millRequestTitle: {
    fontWeight: 'bold',
    color: '#e65100',
    fontSize: '1.1rem',
  },
  millRequestSubtitle: {
    color: '#bf360c',
    fontSize: '0.9rem',
  },
  viewMillRequestsBtn: {
    backgroundColor: '#ff9800',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  statsRow: {
    display: 'flex',
    gap: '0.5rem',
  },
  statBadge: {
    backgroundColor: '#e0e0e0',
    color: '#333',
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  statBadgeMill: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  // Tabs
  tabBar: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    overflowX: 'auto',
    paddingBottom: '0.5rem',
  },
  tab: {
    padding: '0.75rem 1.25rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#666',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  tabActive: {
    padding: '0.75rem 1.25rem',
    border: '2px solid #2D5A27',
    borderRadius: '8px',
    backgroundColor: '#2D5A27',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  // Order List
  orderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  emptyText: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '0.5rem',
  },
  emptySubtext: {
    color: '#666',
  },
  // Mill Request Card
  millRequestCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    borderLeft: '4px solid #ff9800',
  },
  millRequestHeader: {
    backgroundColor: '#fff3e0',
    padding: '1rem 1.5rem',
  },
  millRequestBadge: {
    backgroundColor: '#ff9800',
    color: 'white',
    padding: '0.25rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    marginRight: '0.5rem',
  },
  urgentBadge: {
    backgroundColor: '#dc3545',
    color: 'white',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
  },
  millRequestOrderInfo: {
    marginTop: '0.5rem',
    fontWeight: '600',
    color: '#333',
  },
  millRequestShipInfo: {
    color: '#e65100',
    fontSize: '0.9rem',
    marginTop: '0.25rem',
  },
  millRequestItems: {
    padding: '1rem 1.5rem',
  },
  millRequestItemsTitle: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#666',
    marginBottom: '0.75rem',
  },
  millRequestItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 0',
    borderBottom: '1px solid #eee',
  },
  itemCheckbox: {
    fontSize: '1.25rem',
    color: '#666',
  },
  itemQty: {
    fontWeight: '600',
    color: '#333',
    minWidth: '40px',
  },
  itemName: {
    flex: 1,
    color: '#333',
  },
  itemLocation: {
    color: '#666',
    fontSize: '0.85rem',
  },
  millRequestNotes: {
    backgroundColor: '#fafafa',
    padding: '0.75rem 1.5rem',
    color: '#666',
    fontSize: '0.9rem',
    fontStyle: 'italic',
  },
  millRequestActions: {
    padding: '1rem 1.5rem',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  markPulledBtn: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  // Order Card
  orderCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  orderNumber: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#333',
  },
  orderBadges: {
    display: 'flex',
    gap: '0.5rem',
  },
  lockerBadge: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  instoreBadge: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  lockerNumberBadge: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  orderCustomer: {
    marginBottom: '0.5rem',
  },
  customerName: {
    fontWeight: '500',
    color: '#333',
  },
  customerPhone: {
    color: '#666',
    fontSize: '0.9rem',
  },
  orderItems: {
    color: '#666',
    fontSize: '0.9rem',
    marginBottom: '0.5rem',
  },
  orderTotal: {
    fontWeight: '600',
    color: '#2D5A27',
    marginBottom: '1rem',
  },
  orderStatusLabel: {
    fontSize: '0.8rem',
    color: '#888',
    marginBottom: '0.5rem',
    textTransform: 'capitalize' as const,
  },
  orderActions: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  assignBtn: {
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  readyBtn: {
    backgroundColor: '#ff9800',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  readyBadge: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '600',
  },
  passcodeDisplay: {
    backgroundColor: '#f5f5f5',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
  },
  // 44px min height per STANDARDS.md touch-target rule; the flexWrap on
  // orderActions lets it drop to its own row at 375px rather than overflow.
  // Same sizing as printBtn so the two BC-only actions read as a pair.
  // Amber: this is the action that can move money, so it should not read
  // as routine. 44px min height per STANDARDS.md.
  substituteBtn: {
    backgroundColor: '#b45309',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    minHeight: '44px',
    borderRadius: '6px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  moveToLockerBtn: {
    backgroundColor: '#00695c',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    minHeight: '44px',
    borderRadius: '6px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  printBtn: {
    backgroundColor: '#455a64',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    minHeight: '44px',
    borderRadius: '6px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  printBtnBusy: {
    backgroundColor: '#90a4ae',
    cursor: 'wait',
  },
  pickedUpBtn: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #eee',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#999',
    cursor: 'pointer',
    padding: '0.25rem',
    lineHeight: 1,
  },
  modalBody: {
    padding: '1.5rem',
  },
  modalText: {
    color: '#666',
    marginBottom: '1rem',
  },
  lockerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1rem',
  },
  lockerOption: {
    padding: '1rem',
    border: '2px solid #ddd',
    borderRadius: '12px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.2s',
  },
  lockerOptionSelected: {
    borderColor: '#2D5A27',
    backgroundColor: '#e8f5e9',
  },
  lockerOptionDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    backgroundColor: '#f5f5f5',
  },
  lockerNumber: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
  },
  lockerName: {
    color: '#666',
    fontSize: '0.9rem',
  },
  lockerOccupied: {
    color: '#dc3545',
    fontSize: '0.8rem',
    marginTop: '0.25rem',
  },
  modalFooter: {
    display: 'flex',
    gap: '1rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #eee',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#666',
    fontWeight: '500',
    cursor: 'pointer',
  },
  confirmBtn: {
    flex: 1,
    padding: '0.75rem',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#2D5A27',
    color: '#fff',
    fontWeight: '600',
    cursor: 'pointer',
  },
  // New Order Alert Popup
  newOrderOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    animation: 'fadeIn 0.2s ease-out',
  },
  newOrderPopup: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    padding: '2.5rem 2rem',
    textAlign: 'center',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    maxWidth: '320px',
    width: '90%',
    animation: 'scaleIn 0.3s ease-out',
  },
  newOrderIcon: {
    fontSize: '4rem',
    marginBottom: '1rem',
    animation: 'bounce 0.5s ease-out',
  },
  newOrderTitle: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#2D5A27',
    marginBottom: '0.5rem',
  },
  newOrderSubtitle: {
    fontSize: '1rem',
    color: '#666',
    marginBottom: '1.5rem',
  },
  newOrderButton: {
    backgroundColor: '#2D5A27',
    color: '#fff',
    border: 'none',
    padding: '1rem 2.5rem',
    borderRadius: '10px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
};

// Add CSS animations via style tag
if (typeof document !== 'undefined') {
  const styleId = 'store-orders-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes scaleIn {
        from { transform: scale(0.8); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      @keyframes bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default StoreOrders;
